/**
 * Manages compare submenu filtering, titles, placeholders, and loading states.
 * Extracted from PerformanceAnalyticsContainer to keep file within max-lines.
 */
import { useMemo } from "react";

import { extractCompareItemsFromWatchlistEdges } from "@/features/player-detail/utils/performanceAnalyticsUtils";
import {
  SUBMENU_TITLE_MAP,
  SUBMENU_PLACEHOLDER_MAP,
} from "@/features/player-detail/constants";
import type { CompareItem } from "@/features/player-detail/types";
import type {
  WatchlistListMyUserWatchlistsQuery,
  RosterManagementListPositionsQuery,
  RosterManagementListMyOrganizationsLeaguesQuery,
} from "@/generated/graphql";
import dedupePositionNodes from "@/utils/dedupePositionNodes";

/** Parameters for compare submenu hook — GraphQL data, selection state, and loading flags */
interface UseCompareSubmenuParams {
  readonly watchlistsData?: WatchlistListMyUserWatchlistsQuery;
  readonly positionsData?: RosterManagementListPositionsQuery;
  readonly leaguesData?: RosterManagementListMyOrganizationsLeaguesQuery;
  readonly selectedCompareCategory: CompareItem | null;
  readonly compareSubmenuSearch: string;
  readonly debouncedSearchQuery: string;
  readonly playerSearchResults: readonly CompareItem[];
  readonly playersLoading: boolean;
  readonly watchlistsLoading: boolean;
  readonly leaguesLoading: boolean;
}

/**
 * Derives filtered submenu items, title, placeholder, and loading state
 * from GraphQL query data and current selection.
 * @param params - Compare submenu parameters (see UseCompareSubmenuParams)
 * @param params.watchlistsData - Watchlists query result for building compare items
 * @param params.positionsData - Positions query result for building compare items
 * @param params.leaguesData - Leagues query result for building compare items
 * @param params.selectedCompareCategory - Currently selected compare category (or null)
 * @param params.compareSubmenuSearch - Raw search text entered in the submenu
 * @param params.debouncedSearchQuery - Debounced search text for player search query
 * @param params.playerSearchResults - Player search results from GraphQL
 * @param params.playersLoading - Whether player search query is in flight
 * @param params.watchlistsLoading - Whether watchlists query is in flight
 * @param params.leaguesLoading - Whether leagues query is in flight
 * @returns Filtered items, title, placeholder, and loading flag for the active submenu
 */
const useCompareSubmenu = ({
  watchlistsData,
  positionsData,
  leaguesData,
  selectedCompareCategory,
  compareSubmenuSearch,
  debouncedSearchQuery,
  playerSearchResults,
  playersLoading,
  watchlistsLoading,
  leaguesLoading,
}: UseCompareSubmenuParams) => {
  const compareItemsByCategory = useMemo(
    (): Record<string, readonly CompareItem[]> => ({
      watch_list: extractCompareItemsFromWatchlistEdges(
        watchlistsData?.listMyUserWatchlists?.edges
      ),
      // The full taxonomy exposes legacy duplicate rows (abbreviation: null);
      // dedupe so each position appears once (SE-5481).
      position: dedupePositionNodes(positionsData?.listPositions?.edges).map(
        node => ({ id: node.id, name: node.name })
      ),
      leagues:
        leaguesData?.listMyOrganizationsLeagues?.edges
          ?.filter(edge => edge.node.name !== "")
          .map(edge => ({
            id: edge.node.id,
            name: `${edge.node.country} - ${edge.node.name}`,
          })) ?? [],
    }),
    [watchlistsData, positionsData, leaguesData]
  );

  const filteredSubmenuItems = useMemo(() => {
    if (!selectedCompareCategory) return [];

    if (selectedCompareCategory.id === "players") {
      if (playersLoading || debouncedSearchQuery.length < 2) return [];
      return playerSearchResults;
    }

    const items = compareItemsByCategory[selectedCompareCategory.id] ?? [];
    if (!compareSubmenuSearch.trim()) return items;
    const query = compareSubmenuSearch.toLowerCase();
    return items.filter(item => item.name.toLowerCase().includes(query));
  }, [
    selectedCompareCategory,
    compareSubmenuSearch,
    debouncedSearchQuery,
    playerSearchResults,
    playersLoading,
    compareItemsByCategory,
  ]);

  const submenuTitle = selectedCompareCategory
    ? (SUBMENU_TITLE_MAP[selectedCompareCategory.id] ?? "Select Option")
    : "";
  const submenuSearchPlaceholder = selectedCompareCategory
    ? (SUBMENU_PLACEHOLDER_MAP[selectedCompareCategory.id] ?? "Search...")
    : "Search...";

  const categoryId = selectedCompareCategory?.id;
  const isSubmenuLoading =
    (categoryId === "players" && playersLoading) ||
    (categoryId === "watch_list" && watchlistsLoading) ||
    (categoryId === "leagues" && leaguesLoading);

  return {
    filteredSubmenuItems,
    submenuTitle,
    submenuSearchPlaceholder,
    isSubmenuLoading,
  };
};

export default useCompareSubmenu;
